import React, { useEffect, useState } from 'react';
import { Box, Card, CardContent, Typography, Chip, Button, Divider } from '@mui/material';
import { ArrowBack, Edit, Build, LocationOn, Assignment, CalendarToday, AttachMoney } from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import { equipmentService, Equipment } from '../services/equipmentService';

const formatCurrency = (val?: number) => typeof val === 'number' ? `$${val.toLocaleString()}` : '-';
const formatDate = (d?: Date | string) => {
  if (!d) return '-';
  try { return new Date(d).toLocaleDateString(); } catch { return String(d); }
};

const EquipmentDetail: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [item, setItem] = useState<Equipment | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const load = async () => {
      try {
        if (!id) throw new Error('Invalid equipment id');
        const data = await equipmentService.getById(id);
        setItem(data);
      } catch (e: any) {
        setError(e?.message || 'Failed to load equipment');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id]);

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography>Loading equipment...</Typography>
      </Box>
    );
  }

  if (error || !item) {
    return (
      <Box sx={{ p: 3 }}>
        <Button startIcon={<ArrowBack />} onClick={() => navigate('/equipment')} sx={{ mb: 2 }}>Back</Button>
        <Typography variant="h6" color="error">{error || 'Equipment not found'}</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <Button startIcon={<ArrowBack />} onClick={() => navigate('/equipment')} sx={{ mr: 2 }}>Back</Button>
        <Typography variant="h4" sx={{ flex: 1 }}>Equipment Details</Typography>
        <Button variant="outlined" startIcon={<Edit />}>Edit</Button>
      </Box>

      {/* Summary Card */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Build color="primary" sx={{ mr: 2 }} />
            <Box sx={{ flex: 1 }}>
              <Typography variant="h6">{item.name}</Typography>
              <Typography variant="body2" color="text.secondary">
                {item.model || '-'} • SN: {item.serialNumber || '-'}
              </Typography>
            </Box>
            <Chip label={String(item.status).replace('-', ' ').toUpperCase()} color="primary" />
          </Box>

          <Divider sx={{ my: 2 }} />

          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Assignment color="action" />
                <Box>
                  <Typography variant="caption" color="text.secondary">Type</Typography>
                  <Typography variant="body2">{item.type || '-'}</Typography>
                </Box>
              </Box>
            </Box>
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <LocationOn color="action" />
                <Box>
                  <Typography variant="caption" color="text.secondary">Location</Typography>
                  <Typography variant="body2">{item.location || '-'}</Typography>
                </Box>
              </Box>
            </Box>
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Assignment color="action" />
                <Box>
                  <Typography variant="caption" color="text.secondary">Assigned To</Typography>
                  <Typography variant="body2">{item.assignedTo || '-'}</Typography>
                </Box>
              </Box>
            </Box>
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CalendarToday color="action" />
                <Box>
                  <Typography variant="caption" color="text.secondary">Next Maintenance</Typography>
                  <Typography variant="body2">{formatDate(item.nextMaintenance)}</Typography>
                </Box>
              </Box>
            </Box>
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <AttachMoney color="action" />
                <Box>
                  <Typography variant="caption" color="text.secondary">Value</Typography>
                  <Typography variant="body2">{formatCurrency((item as any).currentValue ?? item.value)}</Typography>
                </Box>
              </Box>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Specifications */}
      {item.specifications && Object.keys(item.specifications).length > 0 && (
        <Card>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 2 }}>Specifications</Typography>
            {Object.entries(item.specifications).map(([key, value]) => (
              <Box key={key} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
                <Typography variant="body2" color="text.secondary">{key}</Typography>
                <Typography variant="body2">{typeof value === 'object' ? JSON.stringify(value) : String(value)}</Typography>
              </Box>
            ))}
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default EquipmentDetail;